import { LogManager } from '@src/utility/Logger';
import { useCallback, useRef } from 'react';

import type { TranscriptActionSource } from '@src/analytics/EventTracker';
import type { SetURLSearchParams } from 'react-router-dom';

const logger = LogManager.getLogger('feature:redline-url-state');

const REDLINE_PARAM = 'redline';

/**
 * The live query string, `?` stripped, for comparison against a snapshot. Empty
 * under a `MemoryRouter`, so `staleBase` reads spuriously true in tests — one
 * reason this stays at debug level.
 */
const liveSearch = () => window.location.search.replace(/^\?/, '');

/**
 * Absence IS the off state, so the writer can delete rather than persist a
 * `redline=0` that would ride along in every shared link. Reader and writer
 * share this so they can't disagree about what absence means.
 */
const readRedline = (params: URLSearchParams): boolean =>
  params.get(REDLINE_PARAM) === '1';

interface RedlineUrlStateOptions {
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
  /** Identifies the instance in the logs; four hosts share this component. */
  surface: TranscriptActionSource;
}

/**
 * The redline / replaced-terms toggle, persisted in the URL (`?redline=1`) so a
 * page refresh restores the view. `replace: true` avoids polluting browser
 * history with every toggle.
 *
 * `setShowHistory` MUST keep a stable identity — see the comment on `latest`.
 * PLAT-11400 was exactly that invariant breaking, and it is pinned by
 * `__tests__/useRedlineUrlState.test.ts`.
 *
 * The one debug line records who moved the param and whether the write was
 * built on a snapshot the URL had already moved past. That matters because
 * `redline` is UNPREFIXED, and both the clip preview
 * (`computeClipPreviewSearch`) and the Transcript & Speakers tab
 * (`buildViewParams`) rebuild the WHOLE query string from a base they are
 * handed — react-router (6.30) gives that base as the render-closure params
 * rather than the live URL, so a writer holding a stale one silently drops this
 * param. `staleBase` is that condition.
 */
export function useRedlineUrlState({
  searchParams,
  setSearchParams,
  surface,
}: RedlineUrlStateOptions): {
  showHistory: boolean;
  setShowHistory: (next: boolean | ((prev: boolean) => boolean)) => void;
} {
  const showHistory = readRedline(searchParams);

  // PLAT-11400 — react-router rebuilds `setSearchParams` on EVERY URL change
  // (its `useCallback` closes over `searchParams`, itself memoized on
  // `location.search`). Read it through a ref so `setShowHistory` keeps ONE
  // identity for this instance's lifetime: `Transcript.tsx`'s Show Only effect
  // lists it as a dependency, and a churning identity re-fired that effect on
  // every URL change — so writing `redline=1` re-triggered the effect that
  // clears redline, and the toggle undid itself on every click.
  //
  // Assigned during render rather than in an effect: an effect would create an
  // ordering dependency with that same Show Only effect (both live in
  // `Transcript`, and this hook only wins today by being declared earlier).
  // The value is never read during render — only inside the callback below —
  // so a render-time assignment cannot tear.
  const latest = useRef({ setSearchParams, surface });
  latest.current = { setSearchParams, surface };

  const setShowHistory = useCallback(
    (next: boolean | ((prev: boolean) => boolean)) => {
      const { setSearchParams: writeParams, surface: logSurface } =
        latest.current;
      writeParams(
        prev => {
          const curr = readRedline(prev);
          const resolved = typeof next === 'function' ? next(curr) : next;
          const p = new URLSearchParams(prev);
          if (resolved) p.set(REDLINE_PARAM, '1');
          else p.delete(REDLINE_PARAM);
          const base = prev.toString();
          const live = liveSearch();
          logger.debug('redline toggle', {
            surface: logSurface,
            from: curr,
            to: resolved,
            base,
            live,
            // True ⇒ this write is built on a snapshot the URL has already
            // moved past, so it will clobber whatever changed in between.
            staleBase: base !== live,
          });
          return p;
        },
        { replace: true },
      );
    },
    // Intentionally empty — everything this reads comes from `latest`, which is
    // what keeps the identity stable. See the comment above.
    [],
  );

  return { showHistory, setShowHistory };
}
